import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import SwiperCore from "swiper";
// import "swiper/css";
// import "swiper/css/thumbs";
// import "swiper/css/zoom";
import { Thumbs, Zoom } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import "@/components/GalleryModal/GalleryModal.css";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import Image from "@/components/ui/Image/Image";
import { ctaClickTracking } from "@/dataLayer/dataLayer";
import useMediaQuery from "@/hooks/useMediaQuery";
import { GalleryModalProps } from "@/types";

const Modal = dynamic(() => import("@/components/ui/Modal/Modal"), { ssr: false });
const ModalContent = dynamic(() => import("@/components/ui/Modal/ModalContent"), { ssr: false });

export const GalleryModal: React.FC<GalleryModalProps> = ({ medias, modalOpen, closeModal }) => {
  const t = useTranslations();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [swiperKey, setSwiperKey] = useState(0);

  useEffect(() => {
    // Clean up the thumbsSwiper instance on close modal to avoid error on re-open
    if (!modalOpen && thumbsSwiper) {
      thumbsSwiper.destroy(true, true);
      setThumbsSwiper(null);
    }
  }, [modalOpen, thumbsSwiper]);

  // @TODO: recive mime type from BE
  function isVideo(url: string) {
    const videoExtensions = [".mp4", ".avi", ".mov", ".wmv", ".flv", ".mkv", ".webm", ".mpeg", ".mpg", ".3gp", ".m4v"];
    url = url.toLowerCase();
    return videoExtensions.some((ext) => url.endsWith(ext));
  }

  const togglePlay = () => {
    if (videoRef.current?.paused) {
      videoRef.current?.play();
      setIsPlaying(true);
    } else {
      console.log(videoRef.current);
      videoRef.current?.pause();
      setIsPlaying(false);
    }
    ctaClickTracking(isPlaying ? "pause" : "play", "GalleryModal", "GalleryModal");
  };

  // @TODO: Gestione video poster?

  return (
    <Modal open={modalOpen} onClose={() => closeModal()}>
      <ModalContent className="h-full mb-0 !rounded-none dialog-content px-m md:px-l py-l md:mb-auto">
        <>
          <div className="flex justify-between pb-l">
            <p className="font-medium uppercase font-primary text-m ">{t("common.label.mediaGallery")}</p>
            <Button
              className="modal__close"
              size={ButtonSize.MEDIUM}
              variant={ButtonVariant.TERTIARY}
              theme={ButtonTheme.BLACK}
              onClick={() => closeModal()}
            >
              <span className="pointer-events-none">
                <Icon name="close" size="24" title="close" className="stroke-black" />
                <span className="sr-only">{t("button.label.close")}</span>
              </span>
            </Button>
          </div>
          <div className="flex flex-row h-[calc(100vh-90px)]">
            {isDesktop && (
              <Swiper
                onSwiper={setThumbsSwiper}
                centeredSlidesBounds={true}
                direction="vertical"
                spaceBetween={6}
                slidesPerView={"auto"}
                watchSlidesProgress={true}
                watchOverflow={true}
                modules={[Thumbs]}
                autoHeight={true}
                mousewheel={true}
                className="w-3\/24"
              >
                {medias?.map((media, i) => (
                  <SwiperSlide key={`unit-gallery-thumb-${i}`} className="overflow-hidden rounded-md">
                    {isVideo(media.url) ? (
                      <div className="w-full h-full relative">
                        <video className="object-cover w-full h-full" muted loop>
                          <source src={media.url} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                        <span className="box-material-secondary absolute translate-y-[-50%] translate-x-[-50%] top-[50%] left-[50%] z-10 h-[44px] w-[44px] rounded-full flex items-center justify-center">
                          <Icon className="stroke-white" name="play" size="24" />
                        </span>
                      </div>
                    ) : (
                      <Image
                        className="aspect-4-3 w-full h-full object-cover rounded-md border border-transparent p-[2px]"
                        src={media.url}
                        alt={media.title}
                        width={190}
                        height={140}
                      />
                    )}
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
            <div className="w-full md:w-21\/24 md:ml-l">
              <Swiper
                spaceBetween={16}
                navigation={false}
                modules={[Thumbs, Zoom]}
                zoom={true}
                direction={"vertical"}
                allowTouchMove={true}
                centeredSlidesBounds={true}
                thumbs={{ swiper: thumbsSwiper }}
                slidesPerView={"auto"}
                breakpoints={{
                  1024: {
                    direction: "horizontal",
                    allowTouchMove: false,
                    slidesPerView: 1,
                    spaceBetween: 24,
                  },
                }}
                className="h-full unit-modal-gallery-swiper"
              >
                {medias?.map((media, i) => {
                  return (
                    <SwiperSlide key={`unit-gallery-${i}`} className="h-auto">
                      {isVideo(media.url) ? (
                        <div className="w-full h-full">
                          <video className="object-cover w-full h-full" autoPlay muted loop playsInline ref={videoRef}>
                            <source src={media.url} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                          <Button
                            size={ButtonSize.SMALL}
                            customClass="md:flex items-center border-none w-fit h-fit p-xs font-medium box-material-primary backdrop-blur-xl overflow-hidden absolute bottom-l right-l rounded-3xl"
                            onClick={togglePlay}
                          >
                            <Icon name={isPlaying ? "pause" : "play"} size="24" title="Pause" className="stroke-white" />
                          </Button>
                        </div>
                      ) : (
                        <div className="swiper-zoom-container">
                          <Image
                            className="md:!object-cover md:h-full md:w-full aspect-4-3 md:aspect-16-9"
                            src={media.url}
                            alt={media.title}
                            width={1500}
                            height={600}
                          />
                        </div>
                      )}
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </div>
          </div>
        </>
      </ModalContent>
    </Modal>
  );
};

export default GalleryModal;
